import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from 'config/api';
import { uploadVenueGalleryImage, updateVenueGallery, deleteVenueGalleryItem } from 'api';

// Import the professional gallery VIEW directly (not the container)
import GalleryView from 'components/organisms/catalogue/tabs/components/gallery/view';

const VenueGalleryContainer = ({ 
  venueData,
  showSnackbar,
  onVenueUpdate 
}) => {
  // State for images (following the same pattern as meeting rooms)
  const [orderedImages, setOrderedImages] = useState([]);
  const [orderedNewImages, setOrderedNewImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Initialize images from venue data (similar to meeting rooms initialization)
  useEffect(() => {
    if (venueData?.gallery) {
      // Transform venue gallery data to match the format expected by the gallery component
      const initialImages = venueData.gallery.map((item) => ({
        id: item.id,
        slug: item.slug,
        url: item.image, // Use 'url' instead of 'image' for consistency
        image: item.image, // Keep 'image' for backward compatibility
        name: `Image ${item.order_line}`,
        order_line: item.order_line,
      }));
      
      // Sort by order_line to maintain proper order
      initialImages.sort((a, b) => a.order_line - b.order_line);
      setOrderedImages(initialImages);
    } else {
      setOrderedImages([]);
    }
  }, [venueData?.gallery]);

  // API helper functions adapted for venue endpoints
  const updateGalleryItem = async (updatedGallery) => {
    try {
      const data = {
        order_line: updatedGallery.order_line,
        slug: updatedGallery.slug,
      };
      await updateVenueGallery(data);
    } catch (err) {
      console.error('Error updating gallery item:', err);
      throw err;
    }
  };

  const deleteGalleryItem = async (data) => {
    try {
      await deleteVenueGalleryItem(data);
    } catch (err) {
      console.error('Error deleting gallery item:', err);
      throw err;
    }
  };

  // Simple showSnackbar fallback if not provided
  const safeShowSnackbar = (message, severity) => {
    if (showSnackbar) {
      showSnackbar(message, severity);
    } else {
    }
  };

  // Function to get unique identifier for an image
  const getImageIdentifier = (image) => {
    return image.slug || image.id || Math.random().toString(36).substring(2);
  };

  // Get proper image URL
  const getImageUrl = (image) => {
    if (!image) return '';

    // For blob URLs (new images), use directly
    if (image.url && image.url.startsWith('blob:')) {
      return image.url;
    }

    // For Django image URLs, add API_BASE_URL if needed (only for relative paths)
    if (image.url) {
      if (image.url.startsWith('http')) return image.url;
      return image.url.startsWith('/') ? `${API_BASE_URL}${image.url}` : image.url;
    }

    if (image.image) {
      if (image.image.startsWith('http')) return image.image;
      return image.image.startsWith('/') ? `${API_BASE_URL}${image.image}` : image.image;
    }

    return '';
  };

  // Handle image upload from file input
  const handleImageUpload = (e) => {
    if (!e.target.files || e.target.files.length === 0) return;

    try {
      const files = Array.from(e.target.files);
      
      const newImageFiles = files.map((file, index) => ({
        id: `new-${Date.now()}-${index}`,
        file: file,
        name: file.name,
        isTemp: true,
        url: URL.createObjectURL(file),
        order_line: orderedImages.length + orderedNewImages.length + index + 1
      }));

      setOrderedNewImages(prev => [...prev, ...newImageFiles]);
      safeShowSnackbar(`${files.length} image(s) added for upload`, 'info');
      
      e.target.value = ''; // Reset file input
    } catch (error) {
      console.error('Error handling image upload:', error);
      safeShowSnackbar('Error uploading images', 'error');
    }
  };

  // Open preview dialog for an image
  const handlePreviewImage = (image) => {
    setPreviewImage(image);
  };

  // Close preview dialog
  const handleClosePreview = () => {
    setPreviewImage(null);
  };

  // Handle closing snackbar (no-op since parent handles it)
  const handleCloseSnackbar = () => {
    // No-op since parent handles snackbar
  };

  const addImageToGallery = async (photo, order_line, venueSlug) => {
    try {
      const result = await uploadVenueGalleryImage(photo, order_line, venueSlug);
      return result;
    } catch (err) {
      console.error('Error uploading image:', err);
      throw err;
    }
  };

  // Handle image deletion - immediate for existing, local for new
  const handleImageDelete = async (index, isNew = false) => {
    try {
      if (isNew) {
        // Remove from new images (no API call needed)
        const currentNewImages = [...orderedNewImages];
        const deletedImage = currentNewImages.splice(index, 1)[0];

        setOrderedNewImages(currentNewImages);

        // Revoke object URL to prevent memory leaks
        if (deletedImage.url && deletedImage.url.startsWith('blob:')) {
          URL.revokeObjectURL(deletedImage.url);
        }

        safeShowSnackbar('Image removed from upload queue', 'success');
      } else {
        // Remove from existing images - immediate API call
        const currentImages = [...orderedImages];
        const deletedImage = currentImages.splice(index, 1)[0];

        // Call delete API first
        if (deletedImage.slug) {
          try {
            await deleteGalleryItem({ slug: deletedImage.slug });
          } catch (error) {
            console.error('Error calling delete API:', error);
            safeShowSnackbar('Failed to delete image from server: ' + error.message, 'error');
            return; // Don't update UI if API call failed
          }
        }

        // Reorder remaining images
        const reorderedImages = currentImages.map((image, idx) => ({
          ...image,
          order_line: idx + 1
        }));

        setOrderedImages(reorderedImages);

        // Update order for all affected images in API
        const updatePromises = reorderedImages
          .filter((img, idx) => img.order_line !== orderedImages[idx]?.order_line)
          .map(img => updateGalleryItem(img));
          
        await Promise.all(updatePromises);
        
        safeShowSnackbar('Image deleted successfully', 'success');

        // Notify parent to refresh venue data
        if (onVenueUpdate) {
          onVenueUpdate();
        }
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      safeShowSnackbar('Failed to delete image: ' + error.message, 'error');
    }
  };

  // Handle moving image left - immediate reordering for existing images
  const handleImageMoveLeft = async (index, isNew = false) => {
    if (index === 0) return;

    try {
      if (isNew) {
        // Just reorder in memory for new images
        const currentImages = [...orderedNewImages];
        [currentImages[index], currentImages[index - 1]] = [currentImages[index - 1], currentImages[index]];
        
        // Update order_line values
        currentImages.forEach((img, idx) => {
          img.order_line = orderedImages.length + idx + 1;
        });
        
        setOrderedNewImages(currentImages);
      } else {
        // Immediate reorder for existing images
        const currentImages = [...orderedImages];
        [currentImages[index], currentImages[index - 1]] = [currentImages[index - 1], currentImages[index]];
        
        // Update order_line values
        currentImages.forEach((img, idx) => {
          img.order_line = idx + 1;
        });
        
        setOrderedImages(currentImages);
        
        // Call reorder API immediately
        try {
          await Promise.all([
            updateGalleryItem(currentImages[index]),
            updateGalleryItem(currentImages[index - 1])
          ]);
          safeShowSnackbar('Images reordered successfully', 'success');
        } catch (error) {
          console.warn('Error reordering images:', error);
          safeShowSnackbar('Image moved but order may not be saved', 'warning');
        }
      }
    } catch (error) {
      console.error('Error moving image:', error);
      safeShowSnackbar('Failed to move image', 'error');
    }
  };

  // Handle moving image right - immediate reordering for existing images
  const handleImageMoveRight = async (index, isNew = false) => {
    const relevantArray = isNew ? orderedNewImages : orderedImages;
    if (index >= relevantArray.length - 1) return;

    try {
      if (isNew) {
        // Just reorder in memory for new images
        const currentImages = [...orderedNewImages];
        [currentImages[index], currentImages[index + 1]] = [currentImages[index + 1], currentImages[index]];
        
        // Update order_line values
        currentImages.forEach((img, idx) => {
          img.order_line = orderedImages.length + idx + 1;
        });
        
        setOrderedNewImages(currentImages);
      } else {
        // Immediate reorder for existing images
        const currentImages = [...orderedImages];
        [currentImages[index], currentImages[index + 1]] = [currentImages[index + 1], currentImages[index]];
        
        // Update order_line values
        currentImages.forEach((img, idx) => {
          img.order_line = idx + 1;
        });
        
        setOrderedImages(currentImages);
        
        // Call reorder API immediately
        try {
          await Promise.all([
            updateGalleryItem(currentImages[index]),
            updateGalleryItem(currentImages[index + 1])
          ]);
          safeShowSnackbar('Images reordered successfully', 'success');
        } catch (error) {
          console.warn('Error reordering images:', error);
          safeShowSnackbar('Image moved but order may not be saved', 'warning');
        }
      }
    } catch (error) {
      console.error('Error moving image:', error);
      safeShowSnackbar('Failed to move image', 'error');
    }
  };

  // Handle saving new images (uploading them to the server)
  const handleSaveGallery = async () => {
    const tempImages = orderedNewImages.filter(img => img.isTemp);
    
    if (tempImages.length === 0) {
      safeShowSnackbar("No new images to upload", "info");
      return;
    }

    try {
      setIsLoading(true);
      

      const uploadedImages = [];
      let successCount = 0;

      for (let i = 0; i < tempImages.length; i++) {
        const image = tempImages[i];

        try {
          
          const result = await addImageToGallery(
            image.file, 
            image.order_line, 
            venueData?.slug
          );


          // Handle different response structures (similar to meeting rooms)
          let imageData = null;

          if (result?.data?.data) {
            imageData = result.data.data;
          } else if (result?.data) {
            imageData = result.data;
          } else if (result) {
            imageData = result;
          }

          if (imageData) {
            uploadedImages.push({
              id: imageData.id,
              slug: imageData.slug,
              url: imageData.image,
              image: imageData.image,
              name: imageData.name || image.name,
              order_line: imageData.order_line
            });
            successCount++;
          }

        } catch (uploadError) {
          console.error(`❌ Error uploading image ${i + 1}:`, uploadError);
          safeShowSnackbar(`Failed to upload ${image.name}: ${uploadError.message}`, "error");
        }
      }

      if (successCount === 0) {
        throw new Error("No images were uploaded successfully");
      }

      // Update orderedImages with the newly uploaded images
      setOrderedImages(prev => {
        const allImages = [...prev, ...uploadedImages].sort((a, b) =>
          (a.order_line || 0) - (b.order_line || 0)
        );
        return allImages;
      });

      // Clear new images after upload
      setOrderedNewImages([]);

      // Revoke object URLs to prevent memory leaks
      tempImages.forEach(img => {
        if (img.url && img.url.startsWith('blob:')) {
          URL.revokeObjectURL(img.url);
        }
      });

      if (successCount === tempImages.length) {
        safeShowSnackbar(`${successCount} image(s) uploaded successfully`, "success");
      } else {
        safeShowSnackbar(`${successCount} of ${tempImages.length} images uploaded successfully`, "warning");
      }

      // Notify parent to refresh venue data
      if (onVenueUpdate) {
        onVenueUpdate();
      }

    } catch (error) {
      console.error("❌ Error uploading images:", error);
      safeShowSnackbar("Failed to upload images: " + error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Local preview state
  const [previewImage, setPreviewImage] = useState(null);

  // Return the professional gallery VIEW with venue-specific logic
  return (
    <GalleryView
      orderedImages={orderedImages}
      orderedNewImages={orderedNewImages}
      isLoadingState={isLoading}
      previewImage={previewImage}
      error={null}
      snackbarState={{ open: false }} // Parent handles snackbar
      disableSaveButton={false} // Enable save button for venue uploads
      getImageIdentifier={getImageIdentifier}
      getImageUrl={getImageUrl}
      handleImageUpload={handleImageUpload}
      handlePreviewImage={handlePreviewImage}
      handleClosePreview={handleClosePreview}
      handleMoveLeft={handleImageMoveLeft}
      handleMoveRight={handleImageMoveRight}
      handleImageDelete={handleImageDelete}
      handleSaveGallery={handleSaveGallery} // Our venue-specific save function
      handleCloseSnackbar={handleCloseSnackbar}
    />
  );
};

export default VenueGalleryContainer;